import type { PluginListenerHandle } from "@capacitor/core";

import type {
  Transport,
  TransportEvents,
  TransportState,
} from "@nav-games/multiplayer";
import {
  CHAR_GUEST_TO_HOST,
  CHAR_HOST_TO_GUEST,
  SERVICE_UUID,
} from "@nav-games/multiplayer";

import type { Role } from "./definitions.js";
import { MultiplayerBle } from "./index.js";

/// `BleTransport` adapts the Capacitor `MultiplayerBle` plugin to the
/// `Transport` contract that `@nav-games/multiplayer`'s `Peer` class
/// expects. Once constructed, `Peer` drives the rest — hello / ready
/// handshake, heartbeats, message framing — without knowing it's
/// talking over BLE at all.
///
/// Construction is two-step so callers can attach lifecycle handlers
/// before any radio activity:
///
///   const t = new BleTransport();
///   const peer = new Peer({ transport: t, role: "host", gameType: "hangman" });
///   peer.onMessage(...);
///   await peer.start();    // → t.open(...) → plugin.start()
///
/// Tear down by calling `peer.close()` which in turn calls
/// `t.close()` and `plugin.stop()`.
export class BleTransport implements Transport {
  private currentState: TransportState = "idle";
  private listeners: PluginListenerHandle[] = [];

  async open(role: "host" | "guest", events: TransportEvents): Promise<void> {
    await MultiplayerBle.initialize({
      role: role as Role,
      serviceUuid: SERVICE_UUID,
      hostToGuestUuid: CHAR_HOST_TO_GUEST,
      guestToHostUuid: CHAR_GUEST_TO_HOST,
    });

    // Permissions — request before start. The plugin's
    // requestPermissions resolves to a status object; if denied we
    // surface as an error before the radio starts so the UI can ask
    // the user to enable Bluetooth in Settings.
    const perm = await MultiplayerBle.requestPermissions();
    if (perm.bluetooth !== "granted") {
      events.onError(new Error("Bluetooth permission denied"));
      this.currentState = "disconnected";
      return;
    }

    // Wire up the plugin's event stream BEFORE start() so we don't
    // race the first `opened` event.
    this.listeners.push(
      await MultiplayerBle.addListener("opened", () => {
        this.currentState = "connected";
        events.onOpen();
      }),
    );
    this.listeners.push(
      await MultiplayerBle.addListener("frame", ({ bytes }) => {
        events.onFrame(Uint8Array.from(bytes));
      }),
    );
    this.listeners.push(
      await MultiplayerBle.addListener("closed", ({ reason }) => {
        this.currentState = "disconnected";
        events.onClose(reason);
      }),
    );
    this.listeners.push(
      await MultiplayerBle.addListener("error", ({ message }) => {
        events.onError(new Error(message));
      }),
    );

    this.currentState = role === "host" ? "advertising" : "scanning";
    await MultiplayerBle.start();
  }

  async send(bytes: Uint8Array): Promise<void> {
    if (this.currentState !== "connected") {
      throw new Error(`BleTransport not connected (state=${this.currentState})`);
    }
    await MultiplayerBle.send({ bytes: Array.from(bytes) });
  }

  async close(): Promise<void> {
    if (this.currentState === "idle" || this.currentState === "disconnected") {
      return;
    }
    this.currentState = "disconnected";
    await MultiplayerBle.stop();
    for (const l of this.listeners) {
      try {
        await l.remove();
      } catch {
        /* listener already gone — fine */
      }
    }
    this.listeners = [];
  }

  state(): TransportState {
    return this.currentState;
  }
}
